import clsx from 'clsx';
import { useState } from 'react';
import {
  formatEvaluationScore,
  getEvaluationScoreTitle,
  getEvaluationScoreColor,
  isNotApplicableScore,
} from '../../../lib/utils';
import { Markdown } from '../markdown';
import DetailBlock from './components/detail-block/detail-block';
import { EvaluationResultCardProps } from './types';
import EvaluationSuccessAndFailure from './components/evaluation-success-and-failure/evaluation-success-and-failure';

function getExampleColor(contribution: 'positive' | 'negative' | 'neutral') {
  if (contribution === 'positive') return 'bg-green-100 text-green-800';
  if (contribution === 'neutral') return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
}

function formatAgentQuote(quote: string) {
  const trimmed = quote.trim();
  if (!trimmed) return quote;
  const wrappedInDouble = trimmed.startsWith('"') && trimmed.endsWith('"');
  const wrappedInSingle = trimmed.startsWith("'") && trimmed.endsWith("'");
  if (wrappedInDouble || wrappedInSingle) return trimmed;
  return `"${trimmed}"`;
}

function EvaluationResultCard({
  evaluator,
  existingResult,
  pendingResult,
}: EvaluationResultCardProps) {
  const [explanationOpen, setExplanationOpen] = useState(true);
  const examples = Array.isArray(existingResult?.examples)
    ? existingResult.examples
    : [];

  return (
    <div
      key={evaluator.id}
      data-onboarding="evaluation-result-card"
      className="rounded border border-[#00000020] bg-prompt-safecomfantasy p-3"
    >
      <div className="mb-2 flex items-center justify-between">
        <h4 className="font-medium text-prompt-safecomvulcan">
          {evaluator.name}
        </h4>
        {existingResult && (
          <span
            className={clsx(
              'rounded px-2 text-sm font-medium',
              getEvaluationScoreColor(existingResult.score),
            )}
            title={getEvaluationScoreTitle(existingResult.score)}
          >
            {formatEvaluationScore(existingResult.score)}
          </span>
        )}
      </div>

      {pendingResult?.status === 'loading' && (
        <p className="text-sm text-prompt-safecomabbey">Evaluating...</p>
      )}

      {pendingResult?.status === 'error' && (
        <p className="text-sm text-red-600">Error: {pendingResult.error}</p>
      )}

      {(existingResult || pendingResult?.status === 'complete') && (
        <>
          {existingResult?.criteria && (
            <div className="space-y-1">
              <p className="mb-2 text-sm text-prompt-safecomabbey">
                {existingResult?.feedback || pendingResult?.feedback}
              </p>
              <h5 className="text-sm font-medium text-prompt-safecomvulcan">
                Evaluation criteria:
              </h5>
              {existingResult.criteria.map((criterion, idx) => (
                <div key={idx} className="text-sm text-prompt-safecomabbey">
                  <span className="font-medium">{criterion.name}:</span>{' '}
                  {criterion.description}
                </div>
              ))}
            </div>
          )}
          {isNotApplicableScore(existingResult?.score) ? (
            <DetailBlock
              title="Explanation"
              content={existingResult?.not_tested}
              emptyText="No explanation was given."
              isOpen={explanationOpen}
              onToggle={() => setExplanationOpen((open) => !open)}
            />
          ) : (
            (existingResult?.success || existingResult?.failure) && (
              <EvaluationSuccessAndFailure
                statement={evaluator?.statement}
                success={existingResult?.success}
                failure={existingResult?.failure}
              />
            )
          )}
          {examples.length > 0 && (
            <div
              className="mt-3 space-y-2"
              data-onboarding="evaluation-examples"
            >
              <h5 className="text-sm font-medium text-prompt-safecomvulcan">
                Examples
              </h5>
              {examples.map((example, idx) => (
                <div
                  key={`${example.quote}-${idx}`}
                  className="space-y-2 rounded border border-[#00000020] bg-prompt-safecomwhite p-2"
                >
                  <div>
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-prompt-safecomabbey">
                        Agent quote
                      </span>
                      <span
                        className={clsx(
                          'shrink-0 rounded px-2 py-0.5 text-xs font-medium capitalize',
                          getExampleColor(example.contribution),
                        )}
                      >
                        {example.contribution}
                      </span>
                    </div>
                    <Markdown
                      content={formatAgentQuote(example.quote)}
                      className="text-sm italic text-prompt-safecomvulcan [&_*]:italic [&_p:first-child]:mt-0"
                    />
                  </div>
                  <div>
                    <span className="mb-0.5 block text-xs font-medium text-prompt-safecomabbey">
                      Explanation
                    </span>
                    <Markdown
                      content={example.explanation}
                      className="text-sm text-prompt-safecomabbey"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EvaluationResultCard;
